import React, { useEffect, useMemo, useState } from "react";
import { Alert, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Typography, message } from "antd";
import { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";

import { CheckoutVariantTarget, type CheckoutVariant } from "@fitness-app/data-models/entities/CheckoutVariant";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";
import { getProductLink } from "@fitness-app/utils/src/products/getProductLink";

import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";
import { productApi } from "~/store/initializeStore";

type ProductSummary = {
  id: string;
  name: string;
  slug: string | null;
  prices: ProductPrice[] | null;
};

type CheckoutVariantRow = CheckoutVariant & {
  product?: ProductSummary | null;
};

type GenerateResult = {
  code: string;
  checkoutUrl?: string;
};

type GeneratePayload = {
  prices: string[];
  email: string;
  amount: number;
  endDate: number;
  checkoutUrl: string;
};

const generateCheckoutCode = async (payload: GeneratePayload): Promise<GenerateResult> => {
  try {
    const { data } = await productApi.post<GenerateResult>("/generate-checkout-code", payload);
    return data;
  } catch (error) {
    const serverMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    throw new Error(serverMessage || "Nie udało się wygenerować kodu", { cause: error });
  }
};

interface Props {
  variant: CheckoutVariantRow | null;
  onClose: () => void;
}

export const GenerateDiscountCodeModal = ({ variant, onClose }: Props): React.ReactElement => {
  const { t } = useTranslation("products");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState<number | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedPrices, setSelectedPrices] = useState<string[]>([]);
  const [result, setResult] = useState<GenerateResult | null>(null);

  // Map the variant's price ids to readable, formatted labels (mirrors useProductPricesOptions).
  const priceOptions = useMemo(() => {
    const priceById = new Map((variant?.product?.prices ?? []).map((price) => [price.id, price]));

    return (variant?.prices ?? []).map((priceId) => {
      const price = priceById.get(priceId);

      if (!price) {
        return { value: priceId, label: priceId };
      }

      const intervalLabel =
        price.type === "recurring" || (price.type === "one_time" && price.accessPeriod)
          ? ` (${t(`products:${price.type}.${price.accessPeriod?.interval}`, {
              count: price.accessPeriod?.interval_count,
            })})`
          : "";
      const amountLabel =
        price.unit_amount != null ? getPriceFormatter(price.currency).format(price.unit_amount / 100) : "";
      const base = `${t(`products:paymentTypeOptions.${price.type}`)}${intervalLabel} – ${amountLabel}`;

      return { value: priceId, label: price.customLabel ? `${base} – ${price.customLabel}` : base };
    });
  }, [variant, t]);

  useEffect(() => {
    if (variant) {
      setEmail("");
      setAmount(null);
      setEndDate(null);
      setSelectedPrices(variant.prices ?? []);
      setResult(null);
    }
  }, [variant]);

  const mutation = useMutation({
    mutationFn: generateCheckoutCode,
    onSuccess: (data) => {
      setResult(data);
      void message.success("Wygenerowano kod zniżkowy");
    },
    onError: (error: Error) => {
      void message.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (!variant) return;

    if (!selectedPrices.length) {
      void message.error("Wybierz co najmniej jedną cenę");
      return;
    }
    if (!email.trim()) {
      void message.error("Podaj adres e-mail");
      return;
    }
    if (!amount || amount <= 0) {
      void message.error("Podaj kwotę większą od zera");
      return;
    }
    if (!endDate) {
      void message.error("Wybierz datę zakończenia okresu początkowego");
      return;
    }

    const checkoutUrl = getProductLink({
      productId: variant.productId,
      domain: import.meta.env.VITE_ECOMMERCE_DOMAIN,
      slug: variant.slug,
      isVariant: true,
      isRenewal: variant.target === CheckoutVariantTarget.Renewal,
    });

    mutation.mutate({
      prices: selectedPrices,
      email: email.trim(),
      amount,
      endDate: endDate.unix(),
      checkoutUrl,
    });
  };

  return (
    <Modal
      title="Generuj kod zniżkowy"
      open={Boolean(variant)}
      onCancel={onClose}
      okText="Generuj"
      okButtonProps={{ loading: mutation.isLoading }}
      onOk={handleSubmit}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Zniżka obowiązuje tylko dla okresu początkowego"
        description="Podana kwota to pełna cena (nie procent) za pierwszy okres rozliczeniowy, obowiązująca do wskazanej daty zakończenia. Po tej dacie subskrypcja odnawia się po standardowej cenie wybranych pozycji."
      />

      <Form layout="vertical">
        <Form.Item label="Produkt">
          <Typography.Text>{variant?.product?.name ?? variant?.productId}</Typography.Text>
        </Form.Item>
        <Form.Item label="Ceny" required>
          <Select
            mode="multiple"
            value={selectedPrices}
            onChange={setSelectedPrices}
            placeholder="Wybierz ceny"
            options={priceOptions}
            optionFilterProp="label"
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item label="Adres e-mail" required>
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="klient@example.com"
          />
        </Form.Item>
        <Form.Item label="Kwota za okres początkowy (PLN)" required>
          <InputNumber
            value={amount ?? undefined}
            onChange={(value) => setAmount(typeof value === "number" ? value : null)}
            min={0.01}
            step={0.01}
            precision={2}
            style={{ width: "100%" }}
            placeholder="199.00"
          />
        </Form.Item>
        <Form.Item label="Data zakończenia okresu początkowego" required>
          <DatePicker showTime value={endDate} onChange={setEndDate} style={{ width: "100%" }} />
        </Form.Item>
      </Form>

      {result && (
        <Alert
          type="success"
          showIcon
          message="Wygenerowano kod zniżkowy"
          description={
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Typography.Text strong>Kod:</Typography.Text>
              <Typography.Paragraph
                copyable={{ text: result.code }}
                style={{ marginBottom: 0, wordBreak: "break-all" }}
              >
                {result.code}
              </Typography.Paragraph>
              {result.checkoutUrl && (
                <>
                  <Typography.Text strong>Link do koszyka:</Typography.Text>
                  <Typography.Paragraph
                    copyable={{ text: result.checkoutUrl }}
                    style={{ marginBottom: 0, wordBreak: "break-all" }}
                  >
                    {result.checkoutUrl}
                  </Typography.Paragraph>
                </>
              )}
            </Space>
          }
        />
      )}
    </Modal>
  );
};
